import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

import ClientDropdown from '@src/components/ClientDropdown';
import { useMessage } from '@src/context/common';
import apiClient from '@src/api/clients';
import emailMappingsApi from '@src/api/emailMappings';
import sftpMappingsApi from '@src/api/sftpMappings';
import apiMappingsApi from '@src/api/apiMappings';
import googleSheetMappingsApi from '@src/api/googleSheetMappings';
import sharePointMappingsApi from '@src/api/sharePointMappings';
import { fetchFeedHealth } from '@src/api/feedHealth';
import FeedRunHistoryCard from './FeedRunHistoryCard';

const generateId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

const CATEGORY = {
  EMAIL: 'email',
  SFTP: 'sftp',
  API: 'api',
  GOOGLE_SHEET: 'google_sheet',
  SHAREPOINT: 'sharepoint',
};

const CATEGORY_LABELS = {
  [CATEGORY.EMAIL]: 'Email',
  [CATEGORY.SFTP]: 'SFTP',
  [CATEGORY.API]: 'API',
  [CATEGORY.GOOGLE_SHEET]: 'Google Sheet',
  [CATEGORY.SHAREPOINT]: 'SharePoint',
};

// Feed types that can be attached to a parent company instead of one client:
// the source carries rows for several sibling brands, and client routing rules
// pick the target client per row. SFTP/SharePoint have no parent_company FK.
const PARENT_SCOPED_CATEGORIES = [
  CATEGORY.EMAIL,
  CATEGORY.API,
  CATEGORY.GOOGLE_SHEET,
];

const categorySupportsParentCompany = (category) =>
  PARENT_SCOPED_CATEGORIES.includes(category);

const resolveCategoryFromParam = (value) => {
  if (
    value === CATEGORY.EMAIL
    || value === CATEGORY.SFTP
    || value === CATEGORY.API
    || value === CATEGORY.GOOGLE_SHEET
    || value === CATEGORY.SHAREPOINT
  ) {
    return value;
  }
  return CATEGORY.EMAIL;
};

// Mirror the worker's method rule (process_api_mappings._resolve_method): an
// explicit transform_params.method wins; otherwise a mapping that carries a
// body template is a POST. Pre-wizard mappings stored the body but never the
// method — showing GET for those (and persisting it on save) is what turned
// the Five Star feed into body-less GETs.
const hasJsonBody = (body) =>
  Boolean(
    body &&
      typeof body === 'object' &&
      !Array.isArray(body) &&
      Object.keys(body).length,
  );

const DEFAULT_FORM = {
  client: '',
  parent_company: '',
  routing_default_client: '',
  mapping_type: '',
  schedule_type: '',
  run_time: '',
  test_emails_text: '',
  include_amounts_in_signature: false,
  email_sender: '',
  cc: '',
  subject: '',
  email_body_contains: '',
  is_embedded_table: false,
  filename_substring: '',
  hostname: '',
  port: 22,
  username: '',
  password: '',
  directory: '',
  api_endpoint: '',
  api_method: 'GET',
  api_key: '',
  json_headers_text: '',
  json_body_template_text: '',
  pagination_param: '',
  page_size_param: '',
  initial_page: 1,
  auth_url: '',
  auth_method: 'POST',
  auth_headers_text: '',
  auth_body_text: '',
  auth_token_path: 'token',
  auth_header_name: 'Authorization',
  auth_token_prefix: 'Bearer ',
  auth_query_param: '',
  spreadsheet_id: '',
  worksheet_range: '',
  site_url: '',
  folder_path: '',
  archived: false,
};

// Category → app1 router slug, used for the targeted archive/enabled PATCH.
const CATEGORY_ENDPOINT = {
  [CATEGORY.EMAIL]: 'email-mappings',
  [CATEGORY.SFTP]: 'sftp-mappings',
  [CATEGORY.API]: 'api-mappings',
  [CATEGORY.GOOGLE_SHEET]: 'google-sheet-mappings',
  [CATEGORY.SHAREPOINT]: 'sharepoint-mappings',
};

// Category → its REST client, so the table's row actions and drawer-hydration
// can resolve the right API without a five-way ternary each time.
const API_BY_CATEGORY = {
  [CATEGORY.EMAIL]: emailMappingsApi,
  [CATEGORY.SFTP]: sftpMappingsApi,
  [CATEGORY.API]: apiMappingsApi,
  [CATEGORY.GOOGLE_SHEET]: googleSheetMappingsApi,
  [CATEGORY.SHAREPOINT]: sharePointMappingsApi,
};

// The six health states emitted by services.feed_health, mapped to a chip
// colour + label. Vocabulary matches the backend verbatim (no remapping) so
// this page and the Data Health board always agree.
const STATE_META = {
  healthy: { label: 'Healthy', color: 'success' },
  overdue: { label: 'Overdue', color: 'warning' },
  degraded: { label: 'Degraded', color: 'warning' },
  failed: { label: 'Failed', color: 'error' },
  pending: { label: 'Monitoring', color: 'info' },
  disabled: { label: 'Paused', color: 'default' },
};

// Status filter / count chips, problems-first.
const STATUS_CHIP_ORDER = [
  { key: 'problems', label: 'Problems', color: 'error' },
  { key: 'failed', label: 'Failed', color: 'error' },
  { key: 'overdue', label: 'Overdue', color: 'warning' },
  { key: 'degraded', label: 'Degraded', color: 'warning' },
  { key: 'pending', label: 'Monitoring', color: 'info' },
  { key: 'healthy', label: 'Healthy', color: 'success' },
  { key: 'disabled', label: 'Paused', color: 'default' },
];

const relativeTime = (iso) => {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff)) return '—';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

// Schedule cadence label for a feed-health row.
const cadenceLabel = (row) => {
  const schedule = (row.schedule_type || '').toLowerCase();
  if (schedule === 'manual') return 'Manual';
  if (schedule === 'hourly') return 'Hourly';
  if (schedule === 'daily') return row.run_time ? `Daily ${row.run_time}` : 'Daily';
  if (!schedule) return '—';
  return schedule.charAt(0).toUpperCase() + schedule.slice(1);
};

// "Rows last run" — both email and pull rows carry it on last_run_summary
// (pull feeds via last_pull_rows); "—" only until a mapping's next run.
const rowsLastRun = (row) => {
  const rows = row?.last_run_summary?.rows;
  return typeof rows === 'number' ? rows : '—';
};

const CONDITION_OPERATORS = [
  { value: 'eq', label: 'Equals', needsValue: true },
  { value: 'contains', label: 'Contains', needsValue: true },
  { value: 'not_empty', label: 'Not empty', needsValue: false },
  { value: 'empty', label: 'Empty', needsValue: false },
  { value: 'is_blank', label: 'Is blank', needsValue: false },
  { value: 'gt_num', label: 'Greater than (number)', needsValue: true },
  { value: 'in', label: 'One of (comma separated)', needsValue: true },
];

const createColumnRow = () => ({
  id: generateId(),
  header: '',
  target: '',
});

const createValueMapRow = () => ({
  id: generateId(),
  match: '',
  result: '',
});

const createConditionRow = () => ({
  id: generateId(),
  field: '',
  operator: 'eq',
  value: '',
});

const createTransformRow = () => ({
  id: generateId(),
  setField: '',
  valueSource: 'field',
  fieldName: '',
  constantValue: '',
  defaultValue: '',
  valueMap: [],
  conditions: [],
});

const createFilterRow = () => ({
  id: generateId(),
  field: '',
  operator: 'eq',
  value: '',
});

const createRoutingRow = () => ({
  id: generateId(),
  client: '',
  field: '',
  operator: 'eq',
  value: '',
});

const FILTER_OPERATORS = [
  { value: 'eq', label: 'Equals', needsValue: true },
  { value: 'in', label: 'One of (comma separated)', needsValue: true },
  { value: 'not_in', label: 'Not one of (comma separated)', needsValue: true },
  { value: 'contains', label: 'Contains', needsValue: true },
  { value: 'not_contains', label: 'Does not contain', needsValue: true },
  { value: 'starts_with', label: 'Starts with', needsValue: true },
  { value: 'ends_with', label: 'Ends with', needsValue: true },
];

// Operators whose value is a single string (vs. the comma-separated list operators).
const SINGLE_VALUE_FILTER_OPERATORS = new Set([
  'eq',
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
]);

const FILTER_OPERATOR_HELPER_TEXT = {
  eq: 'Exact match',
  contains: 'Matches if the value appears anywhere (case-insensitive)',
  not_contains: 'Matches if the value does not appear (case-insensitive)',
  starts_with: 'Case-insensitive prefix match',
  ends_with: 'Case-insensitive suffix match',
};

const filterOperatorHelperText = (operator) =>
  FILTER_OPERATOR_HELPER_TEXT[operator] || 'Comma separated';

const parseCsvHeaders = (text) => {
  if (!text) {
    return [];
  }
  const lines = text.split(/\r?\n/).filter((line) => line.trim().length);
  if (!lines.length) {
    return [];
  }
  const line = lines[0];
  const headers = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      headers.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  if (current) {
    headers.push(current.trim());
  }

  return headers.filter(Boolean);
};

const objectToColumnRows = (mapping) => {
  const rows = [];
  if (mapping && typeof mapping === 'object') {
    Object.entries(mapping).forEach(([header, target]) => {
      if (Array.isArray(target)) {
        target.forEach((value) => {
          rows.push({ id: generateId(), header, target: value });
        });
      } else {
        rows.push({ id: generateId(), header, target });
      }
    });
  }
  return rows.length ? rows : [createColumnRow()];
};

const parseTransformations = (params) => {
  const rows = [];
  const transformations = params?.transformations || [];
  if (!transformations.length) {
    return [createTransformRow()];
  }

  transformations.forEach((entry) => {
    const row = createTransformRow();
    row.setField = entry.set || '';
    if (entry.to && typeof entry.to === 'object' && !Array.isArray(entry.to)) {
      row.valueSource = 'field';
      row.fieldName = Array.isArray(entry.to.field) ? entry.to.field[0] : '';
      row.defaultValue = entry.to.default || '';
      const mapEntries = entry.to.value_map || {};
      row.valueMap = Object.entries(mapEntries).map(([match, result]) => ({
        id: generateId(),
        match,
        result,
      }));
    } else {
      row.valueSource = 'constant';
      row.constantValue = entry.to ?? '';
    }

    const conditions = entry.when?.all || [];
    row.conditions = conditions.map((condition) => {
      const operator = CONDITION_OPERATORS.find((item) =>
        Object.prototype.hasOwnProperty.call(condition, item.value)
      );
      return {
        id: generateId(),
        field: Array.isArray(condition.field) ? condition.field[0] : '',
        operator: operator?.value || 'eq',
        value:
          operator?.needsValue && condition[operator.value] !== undefined
            ? Array.isArray(condition[operator.value])
              ? condition[operator.value].join(', ')
              : String(condition[operator.value])
            : '',
      };
    });

    if (!row.conditions.length) {
      row.conditions = [];
    }
    rows.push(row);
  });

  return rows.length ? rows : [createTransformRow()];
};

const buildColumnMappingPayload = (rows) => {
  const payload = {};
  rows.forEach((row) => {
    if (!row.header || !row.target) {
      return;
    }
    if (payload[row.header]) {
      const existing = payload[row.header];
      if (Array.isArray(existing)) {
        if (!existing.includes(row.target)) {
          existing.push(row.target);
        }
      } else if (existing !== row.target) {
        payload[row.header] = [existing, row.target];
      }
    } else {
      payload[row.header] = row.target;
    }
  });
  return payload;
};

const buildConditionPayload = (condition) => {
  if (!condition.field) {
    return null;
  }
  const base = { field: [condition.field] };
  switch (condition.operator) {
    case 'eq':
      if (!condition.value) {
        return null;
      }
      base.eq = condition.value;
      break;
    case 'contains':
      if (!condition.value) {
        return null;
      }
      base.contains = condition.value;
      break;
    case 'not_empty':
      base.not_empty = true;
      break;
    case 'empty':
      base.empty = true;
      break;
    case 'is_blank':
      base.is_blank = true;
      break;
    case 'gt_num': {
      const num = Number(condition.value);
      if (Number.isNaN(num)) {
        return null;
      }
      base.gt_num = num;
      break;
    }
    case 'in': {
      if (!condition.value) {
        return null;
      }
      const values = condition.value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
      if (!values.length) {
        return null;
      }
      base.in = values;
      break;
    }
    default:
      return null;
  }
  return base;
};

const buildTransformPayload = (rows) => {
  const payload = rows
    .map((row) => {
      if (!row.setField) {
        return null;
      }
      const entry = { set: row.setField };

      if (row.valueSource === 'constant') {
        if (!row.constantValue) {
          return null;
        }
        entry.to = row.constantValue;
      } else {
        if (!row.fieldName) {
          return null;
        }
        const to = { field: [row.fieldName] };
        if (row.valueMap?.length) {
          const valueMap = {};
          row.valueMap.forEach((item) => {
            if (item.match) {
              valueMap[item.match] = item.result || '';
            }
          });
          if (Object.keys(valueMap).length) {
            to.value_map = valueMap;
          }
        }
        if (row.defaultValue) {
          to.default = row.defaultValue;
        }
        entry.to = to;
      }

      const conditionPayloads =
        row.conditions
          ?.map((condition) => buildConditionPayload(condition))
          .filter(Boolean) || [];
      if (conditionPayloads.length) {
        entry.when = { all: conditionPayloads };
      }
      return entry;
    })
    .filter(Boolean);

  return payload.length ? { transformations: payload } : {};
};

const parseFilterRows = (params) => {
  const filters = params?.filter_rows || [];
  if (!Array.isArray(filters) || !filters.length) {
    return [createFilterRow()];
  }
  return filters.map((condition) => {
    const operator =
      Object.keys(condition || {}).find((key) =>
        FILTER_OPERATORS.some((item) => item.value === key)
      ) || 'eq';
    const rawValue = condition?.[operator];
    const value = Array.isArray(rawValue)
      ? rawValue.join(', ')
      : rawValue !== undefined && rawValue !== null
        ? String(rawValue)
        : '';
    return {
      id: generateId(),
      field: Array.isArray(condition?.field)
        ? condition.field[0]
        : condition?.field || '',
      operator,
      value,
    };
  });
};

const buildFilterRowsPayload = (rows) => {
  if (!Array.isArray(rows) || !rows.length) {
    return {};
  }
  const payload = rows
    .map((row) => {
      if (!row.field || !row.operator) {
        return null;
      }
      const base = { field: row.field };
      const trimmedValue = typeof row.value === 'string' ? row.value.trim() : '';
      if (SINGLE_VALUE_FILTER_OPERATORS.has(row.operator)) {
        if (!trimmedValue) {
          return null;
        }
        base[row.operator] = trimmedValue;
      } else if (row.operator === 'in' || row.operator === 'not_in') {
        const values = trimmedValue
          ? trimmedValue
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean)
          : [];
        if (!values.length) {
          return null;
        }
        base[row.operator] = values;
      } else {
        return null;
      }
      return base;
    })
    .filter(Boolean);

  return payload.length ? { filter_rows: payload } : {};
};

const parseClientRouting = (params) => {
  const routing = params?.client_routing || {};
  const rules = Array.isArray(routing.rules) ? routing.rules : [];
  const rows = rules
    .map((rule) => {
      const filters = Array.isArray(rule?.filters) ? rule.filters : [];
      const firstFilter = filters[0] || null;
      if (!firstFilter) {
        return null;
      }
      const operator =
        Object.keys(firstFilter).find((key) =>
          FILTER_OPERATORS.some((item) => item.value === key)
        ) || 'eq';
      const rawValue = firstFilter?.[operator];
      return {
        id: generateId(),
        client:
          rule?.client_id !== undefined && rule?.client_id !== null
            ? String(rule.client_id)
            : '',
        field: Array.isArray(firstFilter?.field)
          ? firstFilter.field[0]
          : firstFilter?.field || '',
        operator,
        value: Array.isArray(rawValue)
          ? rawValue.join(', ')
          : rawValue !== undefined && rawValue !== null
            ? String(rawValue)
            : '',
      };
    })
    .filter(Boolean);

  return {
    defaultClient:
      routing?.default_client_id !== undefined &&
      routing?.default_client_id !== null
        ? String(routing.default_client_id)
        : '',
    rows: rows.length ? rows : [createRoutingRow()],
  };
};

const buildClientRoutingPayload = (rows, defaultClient) => {
  const rules = (Array.isArray(rows) ? rows : [])
    .map((row) => {
      if (!row.client || !row.field || !row.operator) {
        return null;
      }
      const filterPayload = buildFilterRowsPayload([
        { field: row.field, operator: row.operator, value: row.value },
      ]);
      const filters = Array.isArray(filterPayload.filter_rows)
        ? filterPayload.filter_rows
        : [];
      if (!filters.length) {
        return null;
      }
      return {
        client_id: Number(row.client),
        filters,
      };
    })
    .filter(Boolean);

  if (!rules.length && !defaultClient) {
    return {};
  }

  return {
    client_routing: {
      ...(defaultClient ? { default_client_id: Number(defaultClient) } : {}),
      ...(rules.length ? { rules } : {}),
    },
  };
};

export default function EmailMappingBuilderPage() {
  const [searchParams] = useSearchParams();
  const initialCategory = resolveCategoryFromParam(searchParams.get('category'));
  const mappingClientFilter = searchParams.get('client_id') || '';
  const message = useMessage();
  const [metadata, setMetadata] = useState({
    backend_fields: [],
    mapping_types: [],
    schedule_types: [],
  });
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [emailMappings, setEmailMappings] = useState([]);
  const [sftpMappings, setSftpMappings] = useState([]);
  const [apiMappings, setApiMappings] = useState([]);
  const [googleSheetMappings, setGoogleSheetMappings] = useState([]);
  const [sharePointMappings, setSharePointMappings] = useState([]);
  const [listLoading, setListLoading] = useState({
    email: false,
    sftp: false,
    api: false,
    google_sheet: false,
    sharepoint: false,
  });
  const [activeEmailId, setActiveEmailId] = useState(null);
  const [activeSftpId, setActiveSftpId] = useState(null);
  const [activeApiId, setActiveApiId] = useState(null);
  const [activeGoogleSheetId, setActiveGoogleSheetId] = useState(null);
  const [activeSharePointId, setActiveSharePointId] = useState(null);
  const [formValues, setFormValues] = useState(DEFAULT_FORM);
  const [columnRows, setColumnRows] = useState([createColumnRow()]);
  const [transformRows, setTransformRows] = useState([createTransformRow()]);
  const [filterRows, setFilterRows] = useState([createFilterRow()]);
  const [routingRows, setRoutingRows] = useState([createRoutingRow()]);
  const [scopeClients, setScopeClients] = useState([]);
  const [parentCompanyOptions, setParentCompanyOptions] = useState([]);
  const [headerSuggestions, setHeaderSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [runLoading, setRunLoading] = useState(false);
  // "Show archived" toggle — the ref lets the (memoised) loaders read the
  // current value without being re-created on every toggle.
  const [showArchived, setShowArchived] = useState(false);
  const showArchivedRef = useRef(false);

  // --- Health-first table state -------------------------------------------
  // The cross-category master table is fed by the feed-health board (one call
  // covers all five feed types with a per-mapping verdict). The five per-
  // category lists above stay loaded only to hydrate the edit drawer.
  const [feedRows, setFeedRows] = useState([]);
  const [feedBackend, setFeedBackend] = useState({}); // {client_id: {backend_stale, days_stale,…}}
  const [feedRecon, setFeedRecon] = useState({}); // {client_id: {detail, severity}}
  const [healthLoading, setHealthLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // When the row menu's "Diagnose with AI" opened the drawer, the run-history
  // card kicks off the diagnosis immediately instead of waiting for a click.
  const [autoDiagnoseOnOpen, setAutoDiagnoseOnOpen] = useState(false);
  const [filterScope, setFilterScope] = useState(null); // {key,type,id,label} | null = all
  const [filterCategories, setFilterCategories] = useState([]); // [] = all categories
  const [filterState, setFilterState] = useState('all'); // 'all' | 'problems' | a state
  const [searchText, setSearchText] = useState('');
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [rowBusy, setRowBusy] = useState({});
  const [newMenuAnchor, setNewMenuAnchor] = useState(null);
  const [rowMenu, setRowMenu] = useState(null); // {anchorEl, row}
  const activeId =
    activeCategory === CATEGORY.EMAIL
      ? activeEmailId
      : activeCategory === CATEGORY.SFTP
        ? activeSftpId
        : activeCategory === CATEGORY.API
          ? activeApiId
          : activeCategory === CATEGORY.GOOGLE_SHEET
            ? activeGoogleSheetId
            : activeSharePointId;
  const currentMappings =
    activeCategory === CATEGORY.EMAIL
      ? emailMappings
      : activeCategory === CATEGORY.SFTP
        ? sftpMappings
        : activeCategory === CATEGORY.API
          ? apiMappings
          : activeCategory === CATEGORY.GOOGLE_SHEET
            ? googleSheetMappings
            : sharePointMappings;
  const isListLoading = listLoading[activeCategory];

  const resolveMappingType = (category = activeCategory, mappingTypes = metadata.mapping_types) => {
    const options = mappingTypes || [];
    const match = options.find((option) => option.value === category);
    if (match) {
      return match.value;
    }
    return (
      options[0]?.value ||
      (category === CATEGORY.SFTP
        ? 'sftp'
        : category === CATEGORY.API
          ? 'api'
          : category === CATEGORY.GOOGLE_SHEET
            ? 'google_sheet'
            : category === CATEGORY.SHAREPOINT
              ? 'sharepoint'
              : 'email')
    );
  };

  const loadMetadata = useCallback(async () => {
    try {
      const response = await emailMappingsApi.metadata();
      const metadataPayload = response.data || {};
      const preferredMappingType = resolveMappingType(
        activeCategory,
        metadataPayload.mapping_types
      );
      setMetadata(metadataPayload);
      setFormValues((prev) => ({
        ...prev,
        mapping_type: prev.mapping_type || preferredMappingType,
        schedule_type:
          metadataPayload.schedule_types?.[0]?.value ||
          prev.schedule_type ||
          'daily',
      }));
    } catch (error) {
      console.error('Failed to load metadata', error);
      message.error('Unable to load mapping metadata.');
    }
  }, [message, activeCategory]);

  const loadScopeOptions = useCallback(async () => {
    try {
      const aggregated = [];
      let page = 1;
      while (true) {
        const response = await apiClient.request({
          url: 'dashboard/clients/',
          method: 'GET',
          payload: {
            fields: 'id,name,parent_company,parent_company_name,active',
            page_size: 5000,
            page,
          },
        });
        const pageResults = Array.isArray(response.data?.results)
          ? response.data.results
          : Array.isArray(response.data)
            ? response.data
            : [];
        aggregated.push(...pageResults);
        const hasNext = Boolean(response.data?.next) && pageResults.length > 0;
        if (!hasNext) {
          break;
        }
        page += 1;
      }
      const results = aggregated;
      setScopeClients(results);

      const uniqueParents = new Map();
      results.forEach((row) => {
        if (row?.parent_company && row?.parent_company_name) {
          uniqueParents.set(String(row.parent_company), {
            value: String(row.parent_company),
            label: row.parent_company_name,
          });
        }
      });
      setParentCompanyOptions(
        Array.from(uniqueParents.values()).sort((a, b) =>
          a.label.localeCompare(b.label)
        )
      );
    } catch (error) {
      console.error('Failed to load client scope options', error);
      setScopeClients([]);
      setParentCompanyOptions([]);
    }
  }, []);

  const loadEmailMappings = useCallback(async () => {
    setListLoading((prev) => ({ ...prev, [CATEGORY.EMAIL]: true }));
    try {
      const response = await emailMappingsApi.list(
        showArchivedRef.current ? { archived: true } : {}
      );
      const results = Array.isArray(response.data?.results)
        ? response.data.results
        : response.data;
      setEmailMappings(Array.isArray(results) ? results : []);
    } catch (error) {
      console.error('Failed to load mappings', error);
      message.error('Unable to load email mappings.');
    } finally {
      setListLoading((prev) => ({ ...prev, [CATEGORY.EMAIL]: false }));
    }
  }, [message]);

  const loadSftpMappings = useCallback(async () => {
    setListLoading((prev) => ({ ...prev, [CATEGORY.SFTP]: true }));
    try {
      const response = await sftpMappingsApi.list(
        showArchivedRef.current ? { archived: true } : {}
      );
      const results = Array.isArray(response.data?.results)
        ? response.data.results
        : response.data;
      setSftpMappings(Array.isArray(results) ? results : []);
    } catch (error) {
      console.error('Failed to load SFTP mappings', error);
      message.error('Unable to load SFTP mappings.');
    } finally {
      setListLoading((prev) => ({ ...prev, [CATEGORY.SFTP]: false }));
    }
  }, [message]);

  const loadApiMappings = useCallback(async () => {
    setListLoading((prev) => ({ ...prev, [CATEGORY.API]: true }));
    try {
      const response = await apiMappingsApi.list(
        showArchivedRef.current ? { archived: true } : {}
      );
      const results = Array.isArray(response.data?.results)
        ? response.data.results
        : response.data;
      setApiMappings(Array.isArray(results) ? results : []);
    } catch (error) {
      console.error('Failed to load API mappings', error);
      message.error('Unable to load API mappings.');
    } finally {
      setListLoading((prev) => ({ ...prev, [CATEGORY.API]: false }));
    }
  }, [message]);

  const loadGoogleSheetMappings = useCallback(async () => {
    setListLoading((prev) => ({ ...prev, [CATEGORY.GOOGLE_SHEET]: true }));
    try {
      const response = await googleSheetMappingsApi.list(
        showArchivedRef.current ? { archived: true } : {}
      );
      const results = Array.isArray(response.data?.results)
        ? response.data.results
        : response.data;
      setGoogleSheetMappings(Array.isArray(results) ? results : []);
    } catch (error) {
      console.error('Failed to load Google Sheet mappings', error);
      message.error('Unable to load Google Sheet mappings.');
    } finally {
      setListLoading((prev) => ({
        ...prev,
        [CATEGORY.GOOGLE_SHEET]: false,
      }));
    }
  }, [message]);

  const loadSharePointMappings = useCallback(async () => {
    setListLoading((prev) => ({ ...prev, [CATEGORY.SHAREPOINT]: true }));
    try {
      const response = await sharePointMappingsApi.list(
        showArchivedRef.current ? { archived: true } : {}
      );
      const results = Array.isArray(response.data?.results)
        ? response.data.results
        : response.data;
      setSharePointMappings(Array.isArray(results) ? results : []);
    } catch (error) {
      console.error('Failed to load SharePoint mappings', error);
      message.error('Unable to load SharePoint mappings.');
    } finally {
      setListLoading((prev) => ({
        ...prev,
        [CATEGORY.SHAREPOINT]: false,
      }));
    }
  }, [message]);

  // Feed-health board: the table's primary data source. include_archived
  // mirrors the "show archived" toggle so the table is never less complete than
  // the old sidebar. fresh=true bypasses the short server cache after an action.
  const loadFeedHealth = useCallback(
    async ({ fresh = false } = {}) => {
      setHealthLoading(true);
      try {
        const response = await fetchFeedHealth({
          include_archived: showArchivedRef.current ? true : undefined,
          fresh: fresh ? true : undefined,
        });
        const data = response.data || {};
        setFeedRows(Array.isArray(data.feeds) ? data.feeds : []);
        setFeedBackend(data.backend || {});
        setFeedRecon(data.recon || {});
      } catch (error) {
        console.error('Failed to load feed health', error);
        message.error('Unable to load feed health.');
      } finally {
        setHealthLoading(false);
      }
    },
    [message]
  );

  const reloadAllLists = useCallback(
    () =>
      Promise.all([
        loadEmailMappings(),
        loadSftpMappings(),
        loadApiMappings(),
        loadGoogleSheetMappings(),
        loadSharePointMappings(),
      ]),
    [
      loadEmailMappings,
      loadSftpMappings,
      loadApiMappings,
      loadGoogleSheetMappings,
      loadSharePointMappings,
    ]
  );

  useEffect(() => {
    loadMetadata();
    loadScopeOptions();
    loadEmailMappings();
    loadSftpMappings();
    loadApiMappings();
    loadGoogleSheetMappings();
    loadSharePointMappings();
    loadFeedHealth();
    // Dependencies intentionally omitted to avoid repeated fetching loops
    // in StrictMode/dev when callbacks get new identities.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clientsById = useMemo(() => {
    const lookup = {};
    scopeClients.forEach((client) => {
      lookup[String(client.id)] = client;
    });
    return lookup;
  }, [scopeClients]);

  const selectedClientParentCompanyId = useMemo(() => {
    if (!mappingClientFilter) {
      return '';
    }
    return String(clientsById[String(mappingClientFilter)]?.parent_company || '');
  }, [clientsById, mappingClientFilter]);

  const buildDefaultForm = useCallback(
    (category = activeCategory) => ({
      ...DEFAULT_FORM,
      client: mappingClientFilter || '',
      parent_company: categorySupportsParentCompany(category)
        ? selectedClientParentCompanyId
        : '',
      mapping_type: resolveMappingType(category),
      schedule_type:
        metadata.schedule_types?.[0]?.value || 'daily',
    }),
    [
      metadata.schedule_types,
      activeCategory,
      mappingClientFilter,
      selectedClientParentCompanyId,
    ]
  );

  // Inactive (churned) branches are hidden from the routing pickers so the
  // dropdown only lists clients that can still receive rows. A client that an
  // existing rule or the default already points at stays listed, flagged
  // inactive — dropping it would make the effect below blank that rule.
  const routingClientOptions = useMemo(() => {
    const parentId = String(formValues.parent_company || '');
    if (!parentId) {
      return [];
    }
    const referenced = new Set(
      routingRows
        .map((row) => String(row.client || ''))
        .concat(String(formValues.routing_default_client || ''))
        .filter((value) => value !== '')
    );
    return scopeClients
      .filter((client) => String(client?.parent_company || '') === parentId)
      .filter(
        (client) =>
          client?.active !== false || referenced.has(String(client.id))
      )
      .map((client) => ({
        value: String(client.id),
        label:
          client?.active === false ? `${client.name} (inactive)` : client.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [
    scopeClients,
    formValues.parent_company,
    formValues.routing_default_client,
    routingRows,
  ]);

  // Client IDs that already have at least one routing rule configured, so the
  // Target client dropdown can flag which clients are already mapped.
  const routedClientIds = useMemo(
    () =>
      new Set(
        routingRows
          .map((row) => String(row.client || ''))
          .filter((value) => value !== '')
      ),
    [routingRows]
  );

  useEffect(() => {
    if (!categorySupportsParentCompany(activeCategory)) {
      return;
    }
    const allowedClientIds = new Set(routingClientOptions.map((option) => option.value));
    setRoutingRows((prev) => {
      // Hand back the SAME array when nothing was pruned. routingClientOptions
      // depends on routingRows, so a fresh array here re-runs this effect on
      // every render and React throws "Maximum update depth exceeded".
      let changed = false;
      const next = prev.map((row) => {
        if (row.client && !allowedClientIds.has(String(row.client))) {
          changed = true;
          return { ...row, client: '' };
        }
        return row;
      });
      return changed ? next : prev;
    });
    if (
      formValues.routing_default_client &&
      !allowedClientIds.has(String(formValues.routing_default_client))
    ) {
      setFormValues((prev) => ({ ...prev, routing_default_client: '' }));
    }
    if (
      formValues.client &&
      formValues.parent_company &&
      String(clientsById[String(formValues.client)]?.parent_company || '') !==
        String(formValues.parent_company)
    ) {
      setFormValues((prev) => ({ ...prev, client: '' }));
    }
  }, [
    activeCategory,
    routingClientOptions,
    formValues.routing_default_client,
    formValues.client,
    formValues.parent_company,
    clientsById,
  ]);

  useEffect(() => {
    if (!mappingClientFilter || activeId) {
      return;
    }
    setFormValues((prev) =>
      prev.client
        ? prev
        : {
            ...prev,
            client: mappingClientFilter,
            parent_company: categorySupportsParentCompany(activeCategory)
              ? selectedClientParentCompanyId
              : prev.parent_company,
          }
    );
  }, [mappingClientFilter, activeId, activeCategory, selectedClientParentCompanyId]);

  const resetForm = (category = activeCategory) => {
    if (category === CATEGORY.EMAIL) {
      setActiveEmailId(null);
    } else if (category === CATEGORY.SFTP) {
      setActiveSftpId(null);
    } else if (category === CATEGORY.API) {
      setActiveApiId(null);
    } else if (category === CATEGORY.GOOGLE_SHEET) {
      setActiveGoogleSheetId(null);
    } else {
      setActiveSharePointId(null);
    }
    setFormValues(buildDefaultForm(category));
    setColumnRows([createColumnRow()]);
    setTransformRows([createTransformRow()]);
    setFilterRows([createFilterRow()]);
    setRoutingRows([createRoutingRow()]);
    setHeaderSuggestions([]);
  };

  const buildFormFromMapping = (mapping, category = activeCategory) => {
    const baseForm = buildDefaultForm(category);
    const includeAmountsInSignature = Boolean(
      mapping?.transform_params?.dedupe?.include_amounts_in_signature ||
        mapping?.transform_params?.include_amounts_in_signature
    );
    const nextForm = {
      ...baseForm,
      client: mapping?.client || '',
      parent_company:
        mapping?.parent_company ||
        (categorySupportsParentCompany(category)
          ? clientsById[String(mapping?.client || '')]?.parent_company || ''
          : ''),
      routing_default_client: categorySupportsParentCompany(category)
        ? String(
            mapping?.transform_params?.client_routing?.default_client_id || ''
          )
        : '',
      mapping_type: mapping?.mapping_type || baseForm.mapping_type,
      schedule_type: mapping?.schedule_type || baseForm.schedule_type,
      run_time: mapping?.run_time || '',
      test_emails_text: (mapping?.test_emails || []).join(', '),
      include_amounts_in_signature: includeAmountsInSignature,
      filename_substring:
        category === CATEGORY.EMAIL
        || category === CATEGORY.SFTP
        || category === CATEGORY.SHAREPOINT
          ? mapping?.filename_substring || ''
          : '',
      archived: Boolean(mapping?.archived),
    };

    if (category === CATEGORY.EMAIL) {
      Object.assign(nextForm, {
        email_sender: mapping?.email_sender || '',
        cc: mapping?.cc || '',
        subject: mapping?.subject || '',
        email_body_contains: mapping?.email_body_contains || '',
        is_embedded_table: Boolean(mapping?.is_embedded_table),
      });
    } else if (category === CATEGORY.SFTP) {
      Object.assign(nextForm, {
        hostname: mapping?.hostname || '',
        port:
          typeof mapping?.port === 'number' ? mapping.port : DEFAULT_FORM.port,
        username: mapping?.username || '',
        password: mapping?.password || '',
        directory: mapping?.directory || '',
      });
    } else if (category === CATEGORY.API) {
      Object.assign(nextForm, {
        api_endpoint: mapping?.api_endpoint || '',
        api_method: (
          mapping?.transform_params?.method ||
          (hasJsonBody(mapping?.json_body_template)
            ? 'POST'
            : DEFAULT_FORM.api_method)
        ).toUpperCase(),
        api_key: mapping?.api_key || '',
        json_headers_text: mapping?.json_headers
          ? JSON.stringify(mapping.json_headers, null, 2)
          : '',
        json_body_template_text: mapping?.json_body_template
          ? JSON.stringify(mapping.json_body_template, null, 2)
          : '',
        pagination_param: mapping?.pagination_param || '',
        page_size_param: mapping?.page_size_param || '',
        initial_page:
          typeof mapping?.initial_page === 'number'
            ? mapping.initial_page
            : DEFAULT_FORM.initial_page,
        auth_url: mapping?.transform_params?.auth?.url || '',
        auth_method: mapping?.transform_params?.auth?.method || DEFAULT_FORM.auth_method,
        auth_headers_text: mapping?.transform_params?.auth?.headers
          ? JSON.stringify(mapping.transform_params.auth.headers, null, 2)
          : '',
        auth_body_text: mapping?.transform_params?.auth?.body
          ? JSON.stringify(mapping.transform_params.auth.body, null, 2)
          : '',
        auth_token_path: mapping?.transform_params?.auth?.token_path || DEFAULT_FORM.auth_token_path,
        auth_header_name: mapping?.transform_params?.auth?.header_name || DEFAULT_FORM.auth_header_name,
        auth_token_prefix: mapping?.transform_params?.auth?.token_prefix ?? DEFAULT_FORM.auth_token_prefix,
        auth_query_param: mapping?.transform_params?.auth?.query_param || '',
      });
    } else if (category === CATEGORY.GOOGLE_SHEET) {
      Object.assign(nextForm, {
        spreadsheet_id: mapping?.spreadsheet_id || '',
        worksheet_range: mapping?.worksheet_range || '',
      });
    } else if (category === CATEGORY.SHAREPOINT) {
      Object.assign(nextForm, {
        site_url: mapping?.site_url || '',
        folder_path: mapping?.folder_path || '',
        // Read-only connection state, surfaced in the Connect panel.
        connect_token: mapping?.connect_token || '',
        needs_reconnect: Boolean(mapping?.needs_reconnect),
        session_updated_at: mapping?.session_updated_at || '',
      });
    }

    return nextForm;
  };

  const handleSelectMapping = (mapping, category = activeCategory) => {
    if (!mapping) {
      resetForm(category);
      return;
    }
    if (category === CATEGORY.EMAIL) {
      setActiveEmailId(mapping.id);
    } else if (category === CATEGORY.SFTP) {
      setActiveSftpId(mapping.id);
    } else if (category === CATEGORY.API) {
      setActiveApiId(mapping.id);
    } else if (category === CATEGORY.GOOGLE_SHEET) {
      setActiveGoogleSheetId(mapping.id);
    } else {
      setActiveSharePointId(mapping.id);
    }

    setFormValues(buildFormFromMapping(mapping, category));
    setColumnRows(objectToColumnRows(mapping.column_mappings));
    setTransformRows(parseTransformations(mapping.transform_params));
    setFilterRows(parseFilterRows(mapping.transform_params));
    const parsedRouting = parseClientRouting(mapping.transform_params);
    setRoutingRows(parsedRouting.rows);
    setHeaderSuggestions(Object.keys(mapping.column_mappings || {}));
  };

  const handleFieldChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleColumnChange = (rowId, field, value) => {
    setColumnRows((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, [field]: value } : row
      )
    );
  };

  const handleTransformChange = (rowId, field, value) => {
    setTransformRows((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, [field]: value } : row
      )
    );
  };

  const handleConditionChange = (rowId, conditionId, field, value) => {
    setTransformRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) {
          return row;
        }
        const conditions = row.conditions?.length ? row.conditions : [];
        return {
          ...row,
          conditions: conditions.map((condition) =>
            condition.id === conditionId
              ? { ...condition, [field]: value }
              : condition
          ),
        };
      })
    );
  };

  const handleValueMapChange = (rowId, mapId, field, value) => {
    setTransformRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) {
          return row;
        }
        const valueMap = row.valueMap?.length ? row.valueMap : [];
        return {
          ...row,
          valueMap: valueMap.map((entry) =>
            entry.id === mapId ? { ...entry, [field]: value } : entry
          ),
        };
      })
    );
  };

  const addCondition = (rowId) => {
    setTransformRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              conditions: [...(row.conditions || []), createConditionRow()],
            }
          : row
      )
    );
  };

  const removeCondition = (rowId, conditionId) => {
    setTransformRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              conditions: (row.conditions || []).filter(
                (condition) => condition.id !== conditionId
              ),
            }
          : row
      )
    );
  };

  const addValueMap = (rowId) => {
    setTransformRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              valueMap: [...(row.valueMap || []), createValueMapRow()],
            }
          : row
      )
    );
  };

  const removeValueMap = (rowId, mapId) => {
    setTransformRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              valueMap: (row.valueMap || []).filter(
                (entry) => entry.id !== mapId
              ),
            }
          : row
      )
    );
  };

  const handleFilterChange = (rowId, field, value) => {
    setFilterRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, [field]: value } : row))
    );
  };

  const addFilterRow = () => {
    setFilterRows((prev) => [...prev, createFilterRow()]);
  };

  const removeFilterRow = (rowId) => {
    setFilterRows((prev) =>
      prev.length > 1 ? prev.filter((row) => row.id !== rowId) : prev
    );
  };

  const handleRoutingChange = (rowId, field, value) => {
    setRoutingRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, [field]: value } : row))
    );
  };

  const addRoutingRow = () => {
    setRoutingRows((prev) => [...prev, createRoutingRow()]);
  };

  const removeRoutingRow = (rowId) => {
    setRoutingRows((prev) =>
      prev.length > 1 ? prev.filter((row) => row.id !== rowId) : prev
    );
  };

  const handleCsvUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const headers = parseCsvHeaders(reader.result);
      if (!headers.length) {
        message.error('Could not detect column headers.');
        return;
      }
      setHeaderSuggestions((prev) => {
        const merged = new Set([...(prev || []), ...headers]);
        return Array.from(merged);
      });
      message.success('Detected CSV headers.');
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleSave = async () => {
    const isEmail = activeCategory === CATEGORY.EMAIL;
    const isSftp = activeCategory === CATEGORY.SFTP;
    const isApi = activeCategory === CATEGORY.API;
    const isGoogle = activeCategory === CATEGORY.GOOGLE_SHEET;
    const isSharePoint = activeCategory === CATEGORY.SHAREPOINT;
    const mappingTypeFallback = resolveMappingType(activeCategory);
    const parseJsonField = (text, fieldLabel) => {
      if (!text || !text.trim()) {
        return {};
      }
      try {
        const parsed = JSON.parse(text);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          throw new Error('Not an object');
        }
        return parsed;
      } catch (error) {
        console.error(`Failed to parse ${fieldLabel}`, error);
        message.error(`${fieldLabel} must be valid JSON.`);
        throw error;
      }
    };

    const buildAuthPayload = () => {
      if (activeCategory !== CATEGORY.API) {
        return {};
      }
      const hasAny =
        formValues.auth_url ||
        formValues.auth_headers_text?.trim() ||
        formValues.auth_body_text?.trim() ||
        formValues.auth_header_name ||
        formValues.auth_query_param;

      if (!hasAny) {
        return {};
      }

      let parsedHeaders = {};
      let parsedBody = {};
      try {
        parsedHeaders = parseJsonField(
          formValues.auth_headers_text,
          'Auth headers'
        );
        parsedBody = parseJsonField(formValues.auth_body_text, 'Auth body');
      } catch (error) {
        throw error;
      }

      return {
        url: formValues.auth_url,
        method: (formValues.auth_method || 'POST').toUpperCase(),
        headers: parsedHeaders,
        body: parsedBody,
        token_path: formValues.auth_token_path || 'token',
        header_name: formValues.auth_header_name || '',
        token_prefix: formValues.auth_token_prefix ?? '',
        query_param: formValues.auth_query_param || '',
      };
    };

    const supportsParentCompany = categorySupportsParentCompany(activeCategory);
    const payload = {
      client: formValues.client ? Number(formValues.client) : null,
      parent_company:
        supportsParentCompany && formValues.parent_company
          ? Number(formValues.parent_company)
          : null,
      mapping_type:
        formValues.mapping_type || mappingTypeFallback,
      schedule_type:
        formValues.schedule_type ||
        metadata.schedule_types?.[0]?.value ||
        'daily',
      run_time: formValues.run_time || null,
      test_emails: formValues.test_emails_text
        ? formValues.test_emails_text
            .split(',')
            .map((email) => email.trim())
            .filter(Boolean)
        : [],
      column_mappings: buildColumnMappingPayload(columnRows),
      transform_params: (() => {
        const base = {
          ...buildFilterRowsPayload(filterRows),
          ...buildTransformPayload(transformRows),
        };
        if (formValues.include_amounts_in_signature) {
          base.dedupe = {
            ...(base.dedupe || {}),
            include_amounts_in_signature: true,
          };
        }
        if (isApi) {
          // The worker reads transform_params.method (default GET) and only
          // sends json_body_template on POST/PUT/PATCH — persist it explicitly.
          base.method = (formValues.api_method || 'GET').toUpperCase();
          const auth = buildAuthPayload();
          if (Object.keys(auth).length) {
            base.auth = auth;
          }
        }
        if (supportsParentCompany) {
          Object.assign(
            base,
            buildClientRoutingPayload(
              routingRows,
              formValues.routing_default_client
            )
          );
        }
        return base;
      })(),
    };

    if (isEmail) {
      Object.assign(payload, {
        email_sender: formValues.email_sender || '',
        cc: formValues.cc || '',
        subject: formValues.subject || '',
        email_body_contains: formValues.email_body_contains || '',
        is_embedded_table: Boolean(formValues.is_embedded_table),
        filename_substring: formValues.filename_substring || '',
      });
    } else if (isSftp) {
      Object.assign(payload, {
        hostname: formValues.hostname || '',
        port: Number(formValues.port) || DEFAULT_FORM.port,
        username: formValues.username || '',
        password: formValues.password || '',
        directory: formValues.directory || '',
        filename_substring: formValues.filename_substring || '',
      });
    } else if (isApi) {
      let parsedHeaders = {};
      let parsedBody = {};
      try {
        parsedHeaders = parseJsonField(
          formValues.json_headers_text,
          'Headers'
        );
        parsedBody = parseJsonField(
          formValues.json_body_template_text,
          'Body template'
        );
      } catch (error) {
        return;
      }
      Object.assign(payload, {
        api_endpoint: formValues.api_endpoint || '',
        api_key: formValues.api_key || '',
        json_headers: parsedHeaders,
        json_body_template: parsedBody,
        pagination_param: formValues.pagination_param || '',
        page_size_param: formValues.page_size_param || '',
        initial_page:
          formValues.initial_page !== '' &&
          !Number.isNaN(Number(formValues.initial_page))
            ? Number(formValues.initial_page)
            : DEFAULT_FORM.initial_page,
      });
    } else if (isGoogle) {
      Object.assign(payload, {
        spreadsheet_id: formValues.spreadsheet_id || '',
        worksheet_range: formValues.worksheet_range || '',
      });
    } else if (isSharePoint) {
      Object.assign(payload, {
        site_url: formValues.site_url || '',
        folder_path: formValues.folder_path || '',
        filename_substring: formValues.filename_substring || '',
      });
    }

    if (!supportsParentCompany && !payload.client) {
      message.error('Please select a client for this mapping.');
      return;
    }
    if (supportsParentCompany && !payload.client && !payload.parent_company) {
      message.error(
        `Select a client or a parent company for this ${CATEGORY_LABELS[activeCategory]} mapping.`
      );
      return;
    }
    if (
      supportsParentCompany &&
      payload.parent_company &&
      !payload.client &&
      !payload.transform_params?.client_routing?.default_client_id &&
      !(payload.transform_params?.client_routing?.rules || []).length
    ) {
      message.error('Add at least one routed client or a default client for this parent-company mapping.');
      return;
    }
    if (isEmail && !payload.subject) {
      message.error('Please enter a subject to identify the mapping.');
      return;
    }
    if (isSftp) {
      if (!payload.hostname || !payload.username || !payload.directory) {
        message.error('Hostname, username, and directory are required.');
        return;
      }
    }
    if (isApi && !payload.api_endpoint) {
      message.error('Please enter an API endpoint URL.');
      return;
    }
    if (isGoogle) {
      if (!payload.spreadsheet_id || !payload.worksheet_range) {
        message.error('Spreadsheet ID and worksheet range are required.');
        return;
      }
    }
    if (isSharePoint) {
      if (!payload.site_url || !payload.folder_path) {
        message.error('Site URL and folder path are required.');
        return;
      }
    }

    const api = isEmail
      ? emailMappingsApi
      : isSftp
        ? sftpMappingsApi
        : isApi
          ? apiMappingsApi
          : isGoogle
            ? googleSheetMappingsApi
            : sharePointMappingsApi;
    setSaving(true);
    try {
      let newId = activeId;
      if (activeId) {
        await api.update(activeId, payload);
        message.success(
          `${CATEGORY_LABELS[activeCategory]} mapping updated.`
        );
      } else {
        const response = await api.create(payload);
        newId = response.data?.id || null;
        if (isEmail) {
          setActiveEmailId(newId);
        } else if (isSftp) {
          setActiveSftpId(newId);
        } else if (isApi) {
          setActiveApiId(newId);
        } else if (isGoogle) {
          setActiveGoogleSheetId(newId);
        } else {
          setActiveSharePointId(newId);
        }
        message.success(
          `${CATEGORY_LABELS[activeCategory]} mapping created.`
        );
      }
      const reloadFn = isEmail
        ? loadEmailMappings
        : isSftp
          ? loadSftpMappings
          : isApi
            ? loadApiMappings
            : isGoogle
              ? loadGoogleSheetMappings
              : loadSharePointMappings;
      await reloadFn();
      // Refresh the health board so the row's chip / last-run reflects the
      // edit (fresh=1 bypasses the short server cache so we see our own change).
      loadFeedHealth({ fresh: true });
      if (!activeId && newId) {
        setFormValues((prev) => ({ ...prev }));
      }
    } catch (error) {
      console.error('Failed to save mapping', error);
      message.error('Unable to save mapping.');
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    if (!activeId) {
      message.error('Select a mapping to run.');
      return;
    }
    const isEmail = activeCategory === CATEGORY.EMAIL;
    const isSftp = activeCategory === CATEGORY.SFTP;
    const isApi = activeCategory === CATEGORY.API;
    const isGoogle = activeCategory === CATEGORY.GOOGLE_SHEET;
    const apiClient = isEmail
      ? emailMappingsApi
      : isSftp
        ? sftpMappingsApi
        : isApi
          ? apiMappingsApi
          : isGoogle
            ? googleSheetMappingsApi
            : sharePointMappingsApi;
    setRunLoading(true);
    try {
      await apiClient.run(activeId);
      const successMessage = isApi
        ? 'API mapping run acknowledged.'
        : `${CATEGORY_LABELS[activeCategory]} mapping processing has been queued.`;
      message.success(successMessage);
      loadFeedHealth({ fresh: true });
    } catch (error) {
      console.error('Failed to trigger mapping run', error);
      message.error('Unable to start mapping processing.');
    } finally {
      setRunLoading(false);
    }
  };

  const reloadActiveCategory = useCallback(
    () =>
      ({
        [CATEGORY.EMAIL]: loadEmailMappings,
        [CATEGORY.SFTP]: loadSftpMappings,
        [CATEGORY.API]: loadApiMappings,
        [CATEGORY.GOOGLE_SHEET]: loadGoogleSheetMappings,
        [CATEGORY.SHAREPOINT]: loadSharePointMappings,
      }[activeCategory]?.()),
    [
      activeCategory,
      loadEmailMappings,
      loadSftpMappings,
      loadApiMappings,
      loadGoogleSheetMappings,
      loadSharePointMappings,
    ]
  );

  const handleArchiveToggle = async () => {
    if (!activeId) {
      return;
    }
    const slug = CATEGORY_ENDPOINT[activeCategory];
    const next = !formValues.archived;
    try {
      await apiClient.request({
        url: `app1/${slug}/${activeId}/`,
        method: 'PATCH',
        payload: { archived: next },
      });
      message.success(
        next
          ? 'Mapping archived — hidden from the list. Its data is kept.'
          : 'Mapping unarchived.'
      );
      resetForm(activeCategory);
      setDrawerOpen(false);
      await Promise.all([reloadActiveCategory(), loadFeedHealth({ fresh: true })]);
    } catch (error) {
      console.error('Failed to update archive state', error);
      message.error('Unable to update archive state.');
    }
  };

  const handleToggleShowArchived = async (checked) => {
    setShowArchived(checked);
    showArchivedRef.current = checked;
    await Promise.all([reloadAllLists(), loadFeedHealth({ fresh: true })]);
  };

  const handleCategoryChange = (_, value) => {
    if (!value || value === activeCategory) {
      return;
    }
    setActiveCategory(value);
    const targetId =
      value === CATEGORY.EMAIL
        ? activeEmailId
        : value === CATEGORY.SFTP
          ? activeSftpId
          : value === CATEGORY.API
            ? activeApiId
            : value === CATEGORY.GOOGLE_SHEET
              ? activeGoogleSheetId
              : activeSharePointId;
    const targetList =
      value === CATEGORY.EMAIL
        ? emailMappings
        : value === CATEGORY.SFTP
          ? sftpMappings
          : value === CATEGORY.API
            ? apiMappings
            : value === CATEGORY.GOOGLE_SHEET
              ? googleSheetMappings
              : sharePointMappings;
    if (targetId) {
      const target = targetList.find((item) => item.id === targetId);
      if (target) {
        handleSelectMapping(target, value);
        return;
      }
    }
    resetForm(value);
  };

  const usedColumnTargets = useMemo(() => {
    const used = new Set();
    columnRows.forEach((row) => {
      if (row.target) {
        used.add(row.target);
      }
    });
    return used;
  }, [columnRows]);

  const mappingList = useMemo(
    () =>
      (Array.isArray(currentMappings) ? currentMappings : [])
        .filter((mapping) => {
          if (!mappingClientFilter) {
            return true;
          }
          if (String(mapping?.client || '') === String(mappingClientFilter)) {
            return true;
          }
          return (
            selectedClientParentCompanyId &&
            String(mapping?.parent_company || '') === selectedClientParentCompanyId
          );
        })
        .map((mapping) => {
          const label =
            mapping.subject ||
            mapping.directory ||
            mapping.hostname ||
            mapping.api_endpoint ||
            mapping.worksheet_range ||
            mapping.spreadsheet_id ||
            mapping.folder_path ||
            mapping.site_url ||
            'Untitled';
          return {
            ...mapping,
            displayName: mapping.client_name
              ? `${mapping.client_name} · ${label}`
              : mapping.parent_company_name
                ? `${mapping.parent_company_name} · ${label}`
                : label,
          };
        })
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [currentMappings, mappingClientFilter, selectedClientParentCompanyId]
  );

  const duplicateMapping = () => {
    if (!activeId) {
      message.error('Select a mapping to duplicate.');
      return;
    }
    const mappingListSource =
      activeCategory === CATEGORY.EMAIL
        ? emailMappings
        : activeCategory === CATEGORY.SFTP
          ? sftpMappings
          : activeCategory === CATEGORY.API
            ? apiMappings
            : activeCategory === CATEGORY.GOOGLE_SHEET
              ? googleSheetMappings
              : sharePointMappings;
    const mapping = (Array.isArray(mappingListSource)
      ? mappingListSource
      : []
    ).find((item) => item.id === activeId);
    if (!mapping) {
      message.error('Could not find the selected mapping to duplicate.');
      return;
    }

    setFormValues(buildFormFromMapping(mapping, activeCategory));
    setColumnRows(objectToColumnRows(mapping.column_mappings));
    setTransformRows(parseTransformations(mapping.transform_params));
    setFilterRows(parseFilterRows(mapping.transform_params));
    setHeaderSuggestions(Object.keys(mapping.column_mappings || {}));
    setActiveEmailId(null);
    setActiveSftpId(null);
    setActiveApiId(null);
    setActiveGoogleSheetId(null);
    setActiveSharePointId(null);
    message.success('Mapping duplicated. Review and save as a new record.');
  };

  const isEmailCategory = activeCategory === CATEGORY.EMAIL;
  const isSftpCategory = activeCategory === CATEGORY.SFTP;
  const isApiCategory = activeCategory === CATEGORY.API;
  const isGoogleCategory = activeCategory === CATEGORY.GOOGLE_SHEET;
  const isSharePointCategory = activeCategory === CATEGORY.SHAREPOINT;
  const supportsParentCompanyUI = categorySupportsParentCompany(activeCategory);
  const parentSourceNoun = isApiCategory
    ? 'API response'
    : isGoogleCategory
      ? 'sheet'
      : 'inbound report';
  const categoryLabel = CATEGORY_LABELS[activeCategory];
  const refreshHandler =
    activeCategory === CATEGORY.EMAIL
      ? loadEmailMappings
      : isSftpCategory
        ? loadSftpMappings
        : isApiCategory
          ? loadApiMappings
          : isGoogleCategory
            ? loadGoogleSheetMappings
            : loadSharePointMappings;

  // ------------------------------------------------------------------------
  // Health-first table: derived data, selection, and row/bulk actions.
  // ------------------------------------------------------------------------
  const listByCategory = {
    [CATEGORY.EMAIL]: emailMappings,
    [CATEGORY.SFTP]: sftpMappings,
    [CATEGORY.API]: apiMappings,
    [CATEGORY.GOOGLE_SHEET]: googleSheetMappings,
    [CATEGORY.SHAREPOINT]: sharePointMappings,
  };
  const getApi = (category) => API_BY_CATEGORY[category];
  const findLoadedMapping = (category, id) =>
    (listByCategory[category] || []).find((item) => item.id === id);
  const reloadCategory = (category) =>
    ({
      [CATEGORY.EMAIL]: loadEmailMappings,
      [CATEGORY.SFTP]: loadSftpMappings,
      [CATEGORY.API]: loadApiMappings,
      [CATEGORY.GOOGLE_SHEET]: loadGoogleSheetMappings,
      [CATEGORY.SHAREPOINT]: loadSharePointMappings,
    }[category]?.());

  // Scope filter options = every client + every parent company, so the board
  // can be narrowed to one account (answers "which of THIS client's feeds are
  // broken / still need mapping").
  const scopeOptions = useMemo(() => {
    const opts = [];
    scopeClients.forEach((client) =>
      opts.push({ key: `client:${client.id}`, type: 'client', id: client.id, label: client.name })
    );
    parentCompanyOptions.forEach((parent) =>
      opts.push({
        key: `parent:${parent.value}`,
        type: 'parent',
        id: Number(parent.value),
        label: `${parent.label} (parent)`,
      })
    );
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [scopeClients, parentCompanyOptions]);

  // Scope + category + search filters (NOT the status filter — the count chips
  // should show totals within this scope regardless of which status is active).
  const scopedRows = useMemo(() => {
    let out = feedRows;
    if (filterScope) {
      out = out.filter((row) =>
        filterScope.type === 'client'
          ? row.client_pk === filterScope.id
          : row.parent_company_id === filterScope.id
      );
    }
    if (filterCategories.length) {
      const set = new Set(filterCategories);
      out = out.filter((row) => set.has(row.mapping_type));
    }
    const query = searchText.trim().toLowerCase();
    if (query) {
      out = out.filter(
        (row) =>
          (row.label || '').toLowerCase().includes(query) ||
          (row.client_name || '').toLowerCase().includes(query) ||
          JSON.stringify(row.criteria || {}).toLowerCase().includes(query)
      );
    }
    return out;
  }, [feedRows, filterScope, filterCategories, searchText]);

  const tableRows = useMemo(() => {
    if (filterState === 'all') return scopedRows;
    if (filterState === 'problems') return scopedRows.filter((row) => row.is_problem);
    return scopedRows.filter((row) => row.state === filterState);
  }, [scopedRows, filterState]);

  const statusCounts = useMemo(() => {
    const counts = {
      all: scopedRows.length,
      problems: 0,
      healthy: 0,
      overdue: 0,
      degraded: 0,
      failed: 0,
      pending: 0,
      disabled: 0,
    };
    scopedRows.forEach((row) => {
      counts[row.state] = (counts[row.state] || 0) + 1;
      if (row.is_problem) counts.problems += 1;
    });
    return counts;
  }, [scopedRows]);

  // The health row for the mapping currently open in the drawer (for its strip).
  const activeHealthRow = useMemo(
    () =>
      feedRows.find(
        (row) => row.mapping_type === activeCategory && row.mapping_id === activeId
      ) || null,
    [feedRows, activeCategory, activeId]
  );

  const selectedRows = useMemo(
    () => tableRows.filter((row) => selectedKeys.has(row.id)),
    [tableRows, selectedKeys]
  );
  const allSelected = tableRows.length > 0 && selectedRows.length === tableRows.length;
  const someSelected = selectedRows.length > 0 && !allSelected;
  const toggleSelectAll = () =>
    setSelectedKeys((prev) => {
      const everySelected = tableRows.every((row) => prev.has(row.id));
      const next = new Set(prev);
      tableRows.forEach((row) => (everySelected ? next.delete(row.id) : next.add(row.id)));
      return next;
    });
  const toggleRow = (id) =>
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const clearSelection = () => setSelectedKeys(new Set());

  const setBusy = (key, value) => setRowBusy((prev) => ({ ...prev, [key]: value }));

  const backendBadge = (row) => {
    if (!row.client_pk) return null;
    const entry = feedBackend[String(row.client_pk)];
    if (entry && entry.backend_stale) {
      return entry.days_stale != null ? `backend ${entry.days_stale}d` : 'backend stale';
    }
    return null;
  };

  const hydrateDrawerFromMapping = (mapping, category, { asNew = false } = {}) => {
    setFormValues(buildFormFromMapping(mapping, category));
    setColumnRows(objectToColumnRows(mapping.column_mappings));
    setTransformRows(parseTransformations(mapping.transform_params));
    setFilterRows(parseFilterRows(mapping.transform_params));
    setRoutingRows(parseClientRouting(mapping.transform_params).rows);
    setHeaderSuggestions(Object.keys(mapping.column_mappings || {}));
    if (asNew) {
      setActiveEmailId(null);
      setActiveSftpId(null);
      setActiveApiId(null);
      setActiveGoogleSheetId(null);
      setActiveSharePointId(null);
    }
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setAutoDiagnoseOnOpen(false);
  };

  const openRow = async (row, { diagnose = false } = {}) => {
    const category = row.mapping_type;
    setActiveCategory(category);
    setAutoDiagnoseOnOpen(diagnose);
    setDrawerOpen(true);
    const local = findLoadedMapping(category, row.mapping_id);
    if (local) {
      handleSelectMapping(local, category);
      return;
    }
    // Not in the loaded lists (e.g. an archived row while "show archived" is
    // off) — fetch the single record; it stays reachable by id.
    try {
      const response = await getApi(category).retrieve(row.mapping_id);
      handleSelectMapping(response.data, category);
    } catch (error) {
      console.error('Failed to load mapping', error);
      message.error('Unable to load this mapping for editing.');
    }
  };

  const openNewMapping = (category) => {
    setNewMenuAnchor(null);
    setActiveCategory(category);
    resetForm(category);
    setDrawerOpen(true);
  };

  const duplicateRow = async (row) => {
    setRowMenu(null);
    const category = row.mapping_type;
    let mapping = findLoadedMapping(category, row.mapping_id);
    if (!mapping) {
      try {
        mapping = (await getApi(category).retrieve(row.mapping_id)).data;
      } catch (error) {
        message.error('Unable to load this mapping to duplicate.');
        return;
      }
    }
    setActiveCategory(category);
    hydrateDrawerFromMapping(mapping, category, { asNew: true });
    setDrawerOpen(true);
    message.success('Duplicated — review and save as a new mapping.');
  };

  const runRow = async (row) => {
    setRowMenu(null);
    setBusy(row.id, true);
    try {
      await getApi(row.mapping_type).run(row.mapping_id);
      message.success('Run queued.');
      await loadFeedHealth({ fresh: true });
    } catch (error) {
      console.error('Failed to run mapping', error);
      message.error('Unable to start this run.');
    } finally {
      setBusy(row.id, false);
    }
  };

  const patchMapping = (row, payload) =>
    apiClient.request({
      url: `app1/${CATEGORY_ENDPOINT[row.mapping_type]}/${row.mapping_id}/`,
      method: 'PATCH',
      payload,
    });

  const setEnabledRow = async (row, nextEnabled) => {
    setRowMenu(null);
    setBusy(row.id, true);
    try {
      await patchMapping(row, { enabled: nextEnabled });
      message.success(nextEnabled ? 'Feed resumed.' : 'Feed paused.');
      await Promise.all([reloadCategory(row.mapping_type), loadFeedHealth({ fresh: true })]);
    } catch (error) {
      console.error('Failed to update enabled state', error);
      message.error('Unable to update this feed.');
    } finally {
      setBusy(row.id, false);
    }
  };

  const setArchivedRow = async (row, nextArchived) => {
    setRowMenu(null);
    setBusy(row.id, true);
    try {
      await patchMapping(row, { archived: nextArchived });
      message.success(
        nextArchived ? 'Mapping archived. Its data is kept.' : 'Mapping unarchived.'
      );
      await Promise.all([reloadCategory(row.mapping_type), loadFeedHealth({ fresh: true })]);
    } catch (error) {
      console.error('Failed to update archive state', error);
      message.error('Unable to update archive state.');
    } finally {
      setBusy(row.id, false);
    }
  };

  const runBulk = async () => {
    const rows = selectedRows;
    if (!rows.length) return;
    await Promise.allSettled(rows.map((row) => getApi(row.mapping_type).run(row.mapping_id)));
    message.success(`Queued ${rows.length} run(s).`);
    clearSelection();
    await loadFeedHealth({ fresh: true });
  };

  const setEnabledBulk = async (nextEnabled) => {
    const rows = selectedRows;
    if (!rows.length) return;
    await Promise.allSettled(rows.map((row) => patchMapping(row, { enabled: nextEnabled })));
    message.success(`${nextEnabled ? 'Resumed' : 'Paused'} ${rows.length} feed(s).`);
    clearSelection();
    await Promise.all([reloadAllLists(), loadFeedHealth({ fresh: true })]);
  };

  const archiveBulk = async (nextArchived) => {
    const rows = selectedRows;
    if (!rows.length) return;
    await Promise.allSettled(rows.map((row) => patchMapping(row, { archived: nextArchived })));
    message.success(`${nextArchived ? 'Archived' : 'Unarchived'} ${rows.length} mapping(s).`);
    clearSelection();
    await Promise.all([reloadAllLists(), loadFeedHealth({ fresh: true })]);
  };

  const handleRefreshAll = () =>
    Promise.all([reloadAllLists(), loadFeedHealth({ fresh: true })]);

  return (
    <Box p={3}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        mb={2}
      >
        <Box>
          <Typography variant="h4">Back-end data mappings</Typography>
          <Typography variant="body2" color="text.secondary">
            Every inbound feed — email, SFTP, API, Google Sheet, SharePoint — with
            live health. Click a row to edit; the broken ones sort to the top.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showArchived}
                onChange={(event) => handleToggleShowArchived(event.target.checked)}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Show archived
              </Typography>
            }
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleRefreshAll}
            disabled={healthLoading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddCircleOutlineIcon />}
            onClick={(event) => setNewMenuAnchor(event.currentTarget)}
          >
            New mapping
          </Button>
          <Menu
            anchorEl={newMenuAnchor}
            open={Boolean(newMenuAnchor)}
            onClose={() => setNewMenuAnchor(null)}
          >
            {Object.values(CATEGORY).map((category) => (
              <MenuItem key={category} onClick={() => openNewMapping(category)}>
                {CATEGORY_LABELS[category]} mapping
              </MenuItem>
            ))}
          </Menu>
        </Stack>
      </Stack>

      {/* Filter toolbar */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ md: 'center' }}
          >
            <Autocomplete
              size="small"
              sx={{ minWidth: 240 }}
              options={scopeOptions}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              value={filterScope}
              onChange={(_event, value) => setFilterScope(value)}
              renderInput={(params) => (
                <TextField {...params} label="Client / parent" placeholder="All accounts" />
              )}
            />
            <TextField
              size="small"
              sx={{ minWidth: 240 }}
              placeholder="Search subject, host, endpoint…"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ flexGrow: 1 }} />
            <ToggleButtonGroup
              size="small"
              value={filterCategories}
              onChange={(_event, value) => setFilterCategories(value)}
            >
              {Object.values(CATEGORY).map((category) => (
                <ToggleButton key={category} value={category} sx={{ px: 1.5 }}>
                  {CATEGORY_LABELS[category]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
          <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={`All ${statusCounts.all}`}
              color={filterState === 'all' ? 'primary' : 'default'}
              variant={filterState === 'all' ? 'filled' : 'outlined'}
              onClick={() => setFilterState('all')}
            />
            {STATUS_CHIP_ORDER.map((chip) => (
              <Chip
                key={chip.key}
                size="small"
                label={`${chip.label} ${statusCounts[chip.key] ?? 0}`}
                color={filterState === chip.key ? chip.color : 'default'}
                variant={filterState === chip.key ? 'filled' : 'outlined'}
                onClick={() =>
                  setFilterState(filterState === chip.key ? 'all' : chip.key)
                }
              />
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* Bulk action bar */}
      {selectedRows.length > 0 && (
        <Card sx={{ mb: 2, bgcolor: 'action.hover' }}>
          <CardContent
            sx={{ py: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}
          >
            <Typography variant="body2" fontWeight={600}>
              {selectedRows.length} selected
            </Typography>
            <Button size="small" startIcon={<PlayArrowIcon />} onClick={runBulk}>
              Run now
            </Button>
            <Button
              size="small"
              startIcon={<PauseCircleOutlineIcon />}
              onClick={() => setEnabledBulk(false)}
            >
              Pause
            </Button>
            <Button size="small" startIcon={<PlayArrowIcon />} onClick={() => setEnabledBulk(true)}>
              Resume
            </Button>
            <Button
              size="small"
              color="warning"
              startIcon={<ArchiveOutlinedIcon />}
              onClick={() => archiveBulk(true)}
            >
              Archive
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button size="small" onClick={clearSelection}>
              Clear
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Health-first master table */}
      <Card>
        {healthLoading && <LinearProgress />}
        <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleSelectAll}
                  />
                </TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Client / scope</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Mapping</TableCell>
                <TableCell>Last good</TableCell>
                <TableCell align="right">Rows</TableCell>
                <TableCell>Cadence</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableRows.map((row) => {
                const meta = STATE_META[row.state] || { label: row.state, color: 'default' };
                const badge = backendBadge(row);
                const busy = !!rowBusy[row.id];
                return (
                  <TableRow
                    key={row.id}
                    hover
                    selected={selectedKeys.has(row.id)}
                    onClick={() => openRow(row)}
                    sx={{ cursor: 'pointer', opacity: row.archived ? 0.55 : 1 }}
                  >
                    <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={selectedKeys.has(row.id)}
                        onChange={() => toggleRow(row.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        {busy && <CircularProgress size={14} />}
                        {row.needs_reconnect ? (
                          <Chip size="small" color="error" label="Needs reconnect" />
                        ) : (
                          <Chip size="small" color={meta.color} label={meta.label} />
                        )}
                        {row.archived && (
                          <Chip size="small" variant="outlined" label="Archived" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.client_name}</Typography>
                      {badge && (
                        <Tooltip title="This client's backend summary is stale — its data may not be current even if this feed is healthy.">
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            icon={<WarningAmberIcon />}
                            label={badge}
                            sx={{ mt: 0.5 }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={CATEGORY_LABELS[row.mapping_type] || row.mapping_type} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography variant="body2" noWrap title={row.label}>
                        {row.label}
                      </Typography>
                      {(row.is_problem || row.needs_reconnect) && row.reason && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                          title={row.reason}
                        >
                          {row.reason}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {relativeTime(row.last_report_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        {rowsLastRun(row)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {cadenceLabel(row)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                      <IconButton
                        size="small"
                        onClick={(event) => setRowMenu({ anchorEl: event.currentTarget, row })}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!tableRows.length && !healthLoading && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Box p={4} textAlign="center">
                      <Typography variant="body2" color="text.secondary">
                        No mappings match these filters.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Per-row action menu (shared) */}
      <Menu
        anchorEl={rowMenu?.anchorEl}
        open={Boolean(rowMenu)}
        onClose={() => setRowMenu(null)}
      >
        {rowMenu && [
          <MenuItem key="open" onClick={() => { const r = rowMenu.row; setRowMenu(null); openRow(r); }}>
            <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
            Open / edit
          </MenuItem>,
          <MenuItem key="run" onClick={() => runRow(rowMenu.row)}>
            <ListItemIcon><PlayArrowIcon fontSize="small" /></ListItemIcon>
            Run now
          </MenuItem>,
          <MenuItem
            key="diagnose"
            onClick={() => {
              const r = rowMenu.row;
              setRowMenu(null);
              openRow(r, { diagnose: true });
            }}
          >
            <ListItemIcon><AutoAwesomeIcon fontSize="small" /></ListItemIcon>
            Diagnose with AI
          </MenuItem>,
          rowMenu.row.enabled ? (
            <MenuItem key="pause" onClick={() => setEnabledRow(rowMenu.row, false)}>
              <ListItemIcon><PauseCircleOutlineIcon fontSize="small" /></ListItemIcon>
              Pause
            </MenuItem>
          ) : (
            <MenuItem key="resume" onClick={() => setEnabledRow(rowMenu.row, true)}>
              <ListItemIcon><PlayArrowIcon fontSize="small" /></ListItemIcon>
              Resume
            </MenuItem>
          ),
          <MenuItem key="duplicate" onClick={() => duplicateRow(rowMenu.row)}>
            <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
            Duplicate
          </MenuItem>,
          rowMenu.row.archived ? (
            <MenuItem key="unarchive" onClick={() => setArchivedRow(rowMenu.row, false)}>
              <ListItemIcon><UnarchiveOutlinedIcon fontSize="small" /></ListItemIcon>
              Unarchive
            </MenuItem>
          ) : (
            <MenuItem key="archive" onClick={() => setArchivedRow(rowMenu.row, true)}>
              <ListItemIcon><ArchiveOutlinedIcon fontSize="small" /></ListItemIcon>
              Archive
            </MenuItem>
          ),
        ]}
      </Menu>

      {/* Edit / create drawer — the existing builder form, lazy-hydrated */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: 640, md: 860 } } }}
      >
        <Box sx={{ p: 3 }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={2}
            mb={2}
          >
            <Box>
              <Typography variant="h5">
                {activeId ? 'Edit' : 'New'} {CATEGORY_LABELS[activeCategory]} mapping
              </Typography>
              {activeHealthRow ? (
                <Stack direction="row" spacing={1} alignItems="center" mt={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    color={
                      activeHealthRow.needs_reconnect
                        ? 'error'
                        : STATE_META[activeHealthRow.state]?.color || 'default'
                    }
                    label={
                      activeHealthRow.needs_reconnect
                        ? 'Needs reconnect'
                        : STATE_META[activeHealthRow.state]?.label || activeHealthRow.state
                    }
                  />
                  <Typography variant="body2" color="text.secondary">
                    {activeHealthRow.reason}
                  </Typography>
                  {/* When we last LOOKED, next to when data last arrived — the
                      two get conflated, and "is this job even running?" is the
                      first question on a quiet feed. */}
                  <Typography variant="body2" color="text.secondary">
                    ·{' '}
                    {activeHealthRow.last_checked_at
                      ? `last checked ${relativeTime(activeHealthRow.last_checked_at)}`
                      : 'never checked by the scheduler'}
                  </Typography>
                </Stack>
              ) : (
                activeId && (
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    No health signal yet for this mapping.
                  </Typography>
                )
              )}
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              {activeId && (
                <Button
                  size="small"
                  variant="outlined"
                  color={formValues.archived ? 'primary' : 'warning'}
                  startIcon={
                    formValues.archived ? <UnarchiveOutlinedIcon /> : <ArchiveOutlinedIcon />
                  }
                  onClick={handleArchiveToggle}
                >
                  {formValues.archived ? 'Unarchive' : 'Archive'}
                </Button>
              )}
              <IconButton onClick={closeDrawer}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
          <Divider sx={{ mb: 3 }} />
          <Stack spacing={3}>
            {activeId && (
              <FeedRunHistoryCard
                type={activeCategory}
                mappingId={activeId}
                autoDiagnose={autoDiagnoseOnOpen}
              />
            )}
            <Card>
              <CardHeader title="General settings" />
              <Divider />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <ClientDropdown
                      value={formValues.client}
                      onChange={(value) => handleFieldChange('client', value)}
                      label={
                        supportsParentCompanyUI
                          ? 'Direct client (optional)'
                          : 'Select Client'
                      }
                    />
                  </Grid>
                  {supportsParentCompanyUI && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        label="Parent company"
                        value={formValues.parent_company}
                        onChange={(event) =>
                          handleFieldChange('parent_company', event.target.value)
                        }
                        helperText={`Use parent-company scope when one ${parentSourceNoun} should be routed to multiple related clients.`}
                      >
                        <MenuItem value="">None</MenuItem>
                        {parentCompanyOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Mapping type"
                      value={formValues.mapping_type}
                      onChange={(event) =>
                        handleFieldChange('mapping_type', event.target.value)
                      }
                    >
                      {(metadata.mapping_types || []).map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Schedule type"
                      value={formValues.schedule_type}
                      onChange={(event) =>
                        handleFieldChange('schedule_type', event.target.value)
                      }
                    >
                      {(metadata.schedule_types || []).map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="time"
                      label="Run time"
                      InputLabelProps={{ shrink: true }}
                      value={formValues.run_time || ''}
                      onChange={(event) =>
                        handleFieldChange('run_time', event.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Test emails"
                      helperText="Comma separated email addresses that should receive verbose logs."
                      value={formValues.test_emails_text}
                      onChange={(event) =>
                        handleFieldChange(
                          'test_emails_text',
                          event.target.value
                        )
                      }
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    display="flex"
                    alignItems="center"
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(
                            formValues.include_amounts_in_signature
                          )}
                          onChange={(event) =>
                            handleFieldChange(
                              'include_amounts_in_signature',
                              event.target.checked
                            )
                          }
                        />
                      }
                      label="Treat amount variants as distinct records"
                    />
                  </Grid>
                  {isGoogleCategory && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Spreadsheet ID or URL"
                          value={formValues.spreadsheet_id}
                          onChange={(event) =>
                            handleFieldChange(
                              'spreadsheet_id',
                              event.target.value
                            )
                          }
                          helperText="Paste the Google Sheet ID or full URL."
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Worksheet range"
                          value={formValues.worksheet_range}
                          onChange={(event) =>
                            handleFieldChange(
                              'worksheet_range',
                              event.target.value
                            )
                          }
                          helperText='Include the tab name, e.g., "Sheet1!A:Z".'
                          required
                        />
                      </Grid>
                    </>
                  )}
                  {isSharePointCategory && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="SharePoint Site URL"
                          value={formValues.site_url}
                          onChange={(event) =>
                            handleFieldChange('site_url', event.target.value)
                          }
                          helperText="e.g. https://tenant.sharepoint.com/sites/Marketing-External"
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Folder path (server-relative)"
                          value={formValues.folder_path}
                          onChange={(event) =>
                            handleFieldChange('folder_path', event.target.value)
                          }
                          helperText="Starts with /sites/… and includes 'Shared Documents'."
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        {activeId ? (
                          <Box
                            sx={{
                              p: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ mb: 1 }}
                            >
                              <Typography variant="subtitle2">
                                Connection
                              </Typography>
                              <Chip
                                size="small"
                                label={
                                  formValues.needs_reconnect
                                    ? 'Needs reconnect'
                                    : formValues.session_updated_at
                                      ? 'Connected'
                                      : 'Not connected'
                                }
                                color={
                                  formValues.needs_reconnect
                                    ? 'error'
                                    : formValues.session_updated_at
                                      ? 'success'
                                      : 'warning'
                                }
                              />
                            </Stack>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 1 }}
                            >
                              Run this once on your Mac — a Chrome window opens, you
                              sign in (2FA) and the daily pull then runs
                              automatically. Nothing is asked of the client.
                            </Typography>
                            <Box
                              sx={{
                                p: 1.5,
                                bgcolor: 'grey.900',
                                color: 'grey.100',
                                borderRadius: 1,
                                fontFamily: 'monospace',
                                fontSize: 13,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all',
                              }}
                            >
                              {`python3 sharepoint_connect.py --app ${window.location.origin} --mapping ${activeId} --token ${formValues.connect_token || ''}`}
                            </Box>
                            <Button
                              size="small"
                              sx={{ mt: 1 }}
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  `python3 sharepoint_connect.py --app ${window.location.origin} --mapping ${activeId} --token ${formValues.connect_token || ''}`
                                );
                                message.success('Command copied');
                              }}
                            >
                              Copy command
                            </Button>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Save this mapping first — then a one-time Connect
                            command (sign in + 2FA) appears here.
                          </Typography>
                        )}
                      </Grid>
                    </>
                  )}
                  {isEmailCategory && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email sender"
                          value={formValues.email_sender}
                          onChange={(event) =>
                            handleFieldChange('email_sender', event.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="CC email"
                          value={formValues.cc}
                          onChange={(event) =>
                            handleFieldChange('cc', event.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email subject"
                          value={formValues.subject}
                          onChange={(event) =>
                            handleFieldChange('subject', event.target.value)
                          }
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          label="Email body contains"
                          value={formValues.email_body_contains}
                          onChange={(event) =>
                            handleFieldChange(
                              'email_body_contains',
                              event.target.value
                            )
                          }
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        display="flex"
                        alignItems="center"
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formValues.is_embedded_table}
                              onChange={(event) =>
                                handleFieldChange(
                                  'is_embedded_table',
                                  event.target.checked
                                )
                              }
                            />
                          }
                          label="Attachment contains embedded table"
                        />
                      </Grid>
                    </>
                  )}
                  {isSftpCategory && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Hostname"
                          value={formValues.hostname}
                          onChange={(event) =>
                            handleFieldChange('hostname', event.target.value)
                          }
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Port"
                          type="number"
                          value={formValues.port}
                          onChange={(event) =>
                            handleFieldChange('port', event.target.value)
                          }
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Username"
                          value={formValues.username}
                          onChange={(event) =>
                            handleFieldChange('username', event.target.value)
                          }
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="password"
                          label="Password"
                          value={formValues.password}
                          onChange={(event) =>
                            handleFieldChange('password', event.target.value)
                          }
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Directory"
                          value={formValues.directory}
                          onChange={(event) =>
                            handleFieldChange('directory', event.target.value)
                          }
                          required
                        />
                      </Grid>
                    </>
                  )}
                  {isApiCategory && (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="API endpoint URL"
                          value={formValues.api_endpoint}
                          onChange={(event) =>
                            handleFieldChange(
                              'api_endpoint',
                              event.target.value
                            )
                          }
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select
                          fullWidth
                          label="Request method"
                          value={formValues.api_method}
                          onChange={(event) =>
                            handleFieldChange('api_method', event.target.value)
                          }
                          helperText="POST/PUT/PATCH send the body template; GET ignores it."
                        >
                          <MenuItem value="GET">GET</MenuItem>
                          <MenuItem value="POST">POST</MenuItem>
                          <MenuItem value="PUT">PUT</MenuItem>
                          <MenuItem value="PATCH">PATCH</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="password"
                          label="API key"
                          value={formValues.api_key}
                          onChange={(event) =>
                            handleFieldChange('api_key', event.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          label="Headers (JSON)"
                          value={formValues.json_headers_text}
                          onChange={(event) =>
                            handleFieldChange(
                              'json_headers_text',
                              event.target.value
                            )
                          }
                          helperText="JSON object for the request headers."
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          label="Body template (JSON)"
                          value={formValues.json_body_template_text}
                          onChange={(event) =>
                            handleFieldChange(
                              'json_body_template_text',
                              event.target.value
                            )
                          }
                          helperText="JSON payload template for the request body."
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Pagination parameter"
                          value={formValues.pagination_param}
                          onChange={(event) =>
                            handleFieldChange(
                              'pagination_param',
                              event.target.value
                            )
                          }
                          helperText="Optional query parameter to advance pages."
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Page size parameter"
                          value={formValues.page_size_param}
                          onChange={(event) =>
                            handleFieldChange(
                              'page_size_param',
                              event.target.value
                            )
                          }
                          helperText="Optional parameter to control page size."
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Initial page"
                          type="number"
                          value={formValues.initial_page}
                          onChange={(event) =>
                            handleFieldChange(
                              'initial_page',
                              event.target.value
                            )
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                          Auth (optional)
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Auth URL"
                          value={formValues.auth_url}
                          onChange={(event) =>
                            handleFieldChange('auth_url', event.target.value)
                          }
                          helperText="Call this first to retrieve a token."
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select
                          fullWidth
                          label="Auth method"
                          value={formValues.auth_method}
                          onChange={(event) =>
                            handleFieldChange('auth_method', event.target.value)
                          }
                        >
                          <MenuItem value="POST">POST</MenuItem>
                          <MenuItem value="GET">GET</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          label="Auth headers (JSON)"
                          value={formValues.auth_headers_text}
                          onChange={(event) =>
                            handleFieldChange(
                              'auth_headers_text',
                              event.target.value
                            )
                          }
                          helperText='JSON headers for the auth call (e.g., {"Content-Type":"application/json"}).'
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          label="Auth body (JSON)"
                          value={formValues.auth_body_text}
                          onChange={(event) =>
                            handleFieldChange(
                              'auth_body_text',
                              event.target.value
                            )
                          }
                          helperText="JSON payload for the auth request."
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Token path"
                          value={formValues.auth_token_path}
                          onChange={(event) =>
                            handleFieldChange(
                              'auth_token_path',
                              event.target.value
                            )
                          }
                          helperText="Dot path to the token in the auth JSON response (default: token)."
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Header name for token"
                          value={formValues.auth_header_name}
                          onChange={(event) =>
                            handleFieldChange(
                              'auth_header_name',
                              event.target.value
                            )
                          }
                          helperText="e.g., Authorization (leave blank to skip header injection)."
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Token prefix"
                          value={formValues.auth_token_prefix}
                          onChange={(event) =>
                            handleFieldChange(
                              'auth_token_prefix',
                              event.target.value
                            )
                          }
                          helperText='Prepended to the token (e.g., "Bearer ").'
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Query param for token"
                          value={formValues.auth_query_param}
                          onChange={(event) =>
                            handleFieldChange(
                              'auth_query_param',
                              event.target.value
                            )
                          }
                          helperText="If set, token is added as this query parameter instead of a header."
                        />
                      </Grid>
                    </>
                  )}
                  {(isEmailCategory || isSftpCategory || isSharePointCategory) && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Filename substring filter"
                        value={formValues.filename_substring}
                        onChange={(event) =>
                          handleFieldChange(
                            'filename_substring',
                            event.target.value
                          )
                        }
                        helperText="Only process files whose filename contains this text."
                      />
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            <Card>
              <CardHeader
                title="Column mappings"
                subheader="Map CSV headers to backend fields. Upload a sample CSV to help detect headers."
                action={
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadFileIcon />}
                  >
                    Upload sample
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      hidden
                      onChange={handleCsvUpload}
                    />
                  </Button>
                }
              />
              <Divider />
              <CardContent>
                {!!headerSuggestions.length && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
                    {headerSuggestions.map((header) => (
                      <Chip key={header} label={header} variant="outlined" />
                    ))}
                  </Stack>
                )}
                <Stack spacing={2}>
                  {columnRows.map((row) => (
                    <Stack
                      key={row.id}
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={2}
                      alignItems="flex-end"
                    >
                      <TextField
                        label="CSV header"
                        fullWidth
                        value={row.header}
                        onChange={(event) =>
                          handleColumnChange(row.id, 'header', event.target.value)
                        }
                        helperText="Match the exact column title from the file."
                      />
                      <TextField
                        select
                        label="Backend field"
                        fullWidth
                        value={row.target}
                        onChange={(event) =>
                          handleColumnChange(row.id, 'target', event.target.value)
                        }
                      >
                        {(metadata.backend_fields || [])
                          .filter(
                            (field) =>
                              field === row.target ||
                              !usedColumnTargets.has(field)
                          )
                          .map((field) => (
                            <MenuItem key={field} value={field}>
                              {field}
                            </MenuItem>
                          ))}
                      </TextField>
                      <IconButton
                        color="error"
                        onClick={() =>
                          setColumnRows((prev) =>
                            prev.length > 1
                              ? prev.filter((item) => item.id !== row.id)
                              : prev
                          )
                        }
                        disabled={columnRows.length === 1}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
                <Button
                  sx={{ mt: 2 }}
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={() =>
                    setColumnRows((prev) => [...prev, createColumnRow()])
                  }
                >
                  Add mapping
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader
                title="Row filters"
                subheader="Filter incoming rows before processing or transformations."
              />
              <Divider />
              <CardContent>
                <Stack spacing={2}>
                  {filterRows.map((row) => {
                    const operator = FILTER_OPERATORS.find(
                      (item) => item.value === row.operator
                    );
                    return (
                      <Stack
                        key={row.id}
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems="flex-end"
                      >
                        <TextField
                          label="CSV field"
                          fullWidth
                          value={row.field}
                          onChange={(event) =>
                            handleFilterChange(
                              row.id,
                              'field',
                              event.target.value
                            )
                          }
                        />
                        <TextField
                          select
                          label="Operator"
                          value={row.operator}
                          onChange={(event) =>
                            handleFilterChange(
                              row.id,
                              'operator',
                              event.target.value
                            )
                          }
                          sx={{ minWidth: { sm: 200 } }}
                        >
                          {FILTER_OPERATORS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                        {operator?.needsValue && (
                          <TextField
                            label="Value(s)"
                            fullWidth
                            value={row.value}
                            onChange={(event) =>
                              handleFilterChange(
                                row.id,
                                'value',
                                event.target.value
                              )
                            }
                            helperText={filterOperatorHelperText(row.operator)}
                          />
                        )}
                        <IconButton
                          color="error"
                          onClick={() => removeFilterRow(row.id)}
                          disabled={filterRows.length === 1}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Stack>
                    );
                  })}
                </Stack>
                <Button
                  sx={{ mt: 2 }}
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={addFilterRow}
                >
                  Add row filter
                </Button>
              </CardContent>
            </Card>

            {supportsParentCompanyUI && formValues.parent_company && (
              <Card>
                <CardHeader
                  title="Client routing"
                  subheader={`Route rows from one parent-company ${parentSourceNoun} into specific child clients.`}
                />
                <Divider />
                <CardContent>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        label="Default client"
                        value={formValues.routing_default_client}
                        onChange={(event) =>
                          handleFieldChange(
                            'routing_default_client',
                            event.target.value
                          )
                        }
                        helperText="Optional fallback when no routing rule matches."
                      >
                        <MenuItem value="">No default client</MenuItem>
                        {routingClientOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                  <Stack spacing={2}>
                    {routingRows.map((row) => {
                      const operator = FILTER_OPERATORS.find(
                        (item) => item.value === row.operator
                      );
                      return (
                        <Stack
                          key={row.id}
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={2}
                          alignItems="flex-end"
                        >
                          <TextField
                            select
                            label="Target client"
                            fullWidth
                            value={row.client}
                            onChange={(event) =>
                              handleRoutingChange(
                                row.id,
                                'client',
                                event.target.value
                              )
                            }
                          >
                            <MenuItem value="">Select client</MenuItem>
                            {routingClientOptions.map((option) => {
                              const alreadyRouted =
                                routedClientIds.has(option.value) &&
                                option.value !== row.client;
                              return (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.75,
                                      width: '100%',
                                    }}
                                  >
                                    <span>{option.label}</span>
                                    {alreadyRouted && (
                                      <CheckCircleIcon
                                        fontSize="small"
                                        color="success"
                                        titleAccess="Already has a routing rule"
                                        sx={{ ml: 'auto' }}
                                      />
                                    )}
                                  </Box>
                                </MenuItem>
                              );
                            })}
                          </TextField>
                          <TextField
                            label="CSV field"
                            fullWidth
                            value={row.field}
                            onChange={(event) =>
                              handleRoutingChange(
                                row.id,
                                'field',
                                event.target.value
                              )
                            }
                          />
                          <TextField
                            select
                            label="Operator"
                            value={row.operator}
                            onChange={(event) =>
                              handleRoutingChange(
                                row.id,
                                'operator',
                                event.target.value
                              )
                            }
                            sx={{ minWidth: { sm: 200 } }}
                          >
                            {FILTER_OPERATORS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                          {operator?.needsValue && (
                            <TextField
                              label="Value(s)"
                              fullWidth
                              value={row.value}
                              onChange={(event) =>
                                handleRoutingChange(
                                  row.id,
                                  'value',
                                  event.target.value
                                )
                              }
                              helperText={filterOperatorHelperText(row.operator)}
                            />
                          )}
                          <IconButton
                            color="error"
                            onClick={() => removeRoutingRow(row.id)}
                            disabled={routingRows.length === 1}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Stack>
                      );
                    })}
                  </Stack>
                  <Button
                    sx={{ mt: 2 }}
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={addRoutingRow}
                  >
                    Add routing rule
                  </Button>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader
                title="Transform rules"
                subheader="Add conditional logic to populate fields after the initial mapping."
              />
              <Divider />
              <CardContent>
                <Stack spacing={3}>
                  {transformRows.map((row) => (
                    <Box
                      key={row.id}
                      sx={{
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        p: 2,
                      }}
                    >
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={{ sm: 'center' }}
                        mb={2}
                      >
                        <TextField
                          label="Set backend field"
                          fullWidth
                          value={row.setField}
                          onChange={(event) =>
                            handleTransformChange(
                              row.id,
                              'setField',
                              event.target.value
                            )
                          }
                        />
                        <TextField
                          select
                          label="Value source"
                          value={row.valueSource}
                          onChange={(event) =>
                            handleTransformChange(
                              row.id,
                              'valueSource',
                              event.target.value
                            )
                          }
                        >
                          <MenuItem value="field">Copy from CSV field</MenuItem>
                          <MenuItem value="constant">Use constant value</MenuItem>
                        </TextField>
                        <IconButton
                          color="error"
                          onClick={() =>
                            setTransformRows((prev) =>
                              prev.length > 1
                                ? prev.filter((item) => item.id !== row.id)
                                : prev
                            )
                          }
                          disabled={transformRows.length === 1}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Stack>

                      {row.valueSource === 'field' ? (
                        <Stack spacing={2} mb={2}>
                          <TextField
                            label="CSV field"
                            value={row.fieldName}
                            onChange={(event) =>
                              handleTransformChange(
                                row.id,
                                'fieldName',
                                event.target.value
                              )
                            }
                          />
                          <TextField
                            label="Default value"
                            value={row.defaultValue}
                            onChange={(event) =>
                              handleTransformChange(
                                row.id,
                                'defaultValue',
                                event.target.value
                              )
                            }
                            helperText="Optional fallback if no match is found."
                          />
                          <Stack spacing={1}>
                            {(row.valueMap || []).map((entry) => (
                              <Stack
                                key={entry.id}
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1}
                                alignItems="flex-end"
                              >
                                <TextField
                                  label="If CSV value is"
                                  value={entry.match}
                                  onChange={(event) =>
                                    handleValueMapChange(
                                      row.id,
                                      entry.id,
                                      'match',
                                      event.target.value
                                    )
                                  }
                                />
                                <TextField
                                  label="Set to"
                                  value={entry.result}
                                  onChange={(event) =>
                                    handleValueMapChange(
                                      row.id,
                                      entry.id,
                                      'result',
                                      event.target.value
                                    )
                                  }
                                />
                                <IconButton
                                  color="error"
                                  onClick={() =>
                                    removeValueMap(row.id, entry.id)
                                  }
                                >
                                  <DeleteOutlineIcon />
                                </IconButton>
                              </Stack>
                            ))}
                            <Button
                              startIcon={<AddCircleOutlineIcon />}
                              onClick={() => addValueMap(row.id)}
                              variant="outlined"
                              size="small"
                              sx={{ alignSelf: 'flex-start' }}
                            >
                              Add value mapping
                            </Button>
                          </Stack>
                        </Stack>
                      ) : (
                        <TextField
                          label="Constant value"
                          value={row.constantValue}
                          onChange={(event) =>
                            handleTransformChange(
                              row.id,
                              'constantValue',
                              event.target.value
                            )
                          }
                          fullWidth
                          sx={{ mb: 2 }}
                        />
                      )}

                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Conditions
                      </Typography>
                      <Stack spacing={2}>
                        {(row.conditions || []).map((condition) => {
                          const operator = CONDITION_OPERATORS.find(
                            (item) => item.value === condition.operator
                          );
                          return (
                            <Stack
                              key={condition.id}
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={2}
                              alignItems="flex-end"
                            >
                              <TextField
                                label="CSV field"
                                value={condition.field}
                                onChange={(event) =>
                                  handleConditionChange(
                                    row.id,
                                    condition.id,
                                    'field',
                                    event.target.value
                                  )
                                }
                              />
                              <TextField
                                select
                                label="Operator"
                                value={condition.operator}
                                onChange={(event) =>
                                  handleConditionChange(
                                    row.id,
                                    condition.id,
                                    'operator',
                                    event.target.value
                                  )
                                }
                              >
                                {CONDITION_OPERATORS.map((item) => (
                                  <MenuItem key={item.value} value={item.value}>
                                    {item.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                              {operator?.needsValue && (
                                <TextField
                                  label="Value"
                                  value={condition.value}
                                  onChange={(event) =>
                                    handleConditionChange(
                                      row.id,
                                      condition.id,
                                      'value',
                                      event.target.value
                                    )
                                  }
                                />
                              )}
                              <IconButton
                                color="error"
                                onClick={() =>
                                  removeCondition(row.id, condition.id)
                                }
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Stack>
                          );
                        })}
                        <Button
                          startIcon={<AddCircleOutlineIcon />}
                          onClick={() => addCondition(row.id)}
                          variant="outlined"
                          size="small"
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          Add condition
                        </Button>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
                <Button
                  sx={{ mt: 2 }}
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={() =>
                    setTransformRows((prev) => [...prev, createTransformRow()])
                  }
                >
                  Add transformation
                </Button>
              </CardContent>
            </Card>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={handleRunNow}
                disabled={!activeId || runLoading}
              >
                {runLoading ? 'Queueing…' : 'Run now'}
              </Button>
              <Button variant="outlined" onClick={resetForm}>
                Reset
              </Button>
              <Button
                variant="outlined"
                onClick={duplicateMapping}
                disabled={!activeId}
              >
                Duplicate
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
              >
                {activeId ? 'Update mapping' : 'Create mapping'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Drawer>
    </Box>
  );
}
